import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Lock, Pencil, Plus } from "lucide-react";
import { loadProgress } from "../state/progress";
import { finalizePastDays, loadDailyMoodMap, moodValueToColor } from "../lib/mood";
import { advanceDays, advanceWeeks, advanceThirtyDays, advanceYearPlus, resetOffset } from "../lib/timewarp";

const UNLOCK_KEY = "rl_tracker_unlocked_weeks_v1";
const DIARY_LEGACY_KEY = "rl_diary_v1"; // old string-per-day
const DIARY_POSTS_KEY = "rl_diary_posts_v1"; // { "YYYY-MM-DD": [ {id, ts, title, type, content, imageUrl} ] }

function toDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function diaryAvailable() {
  try {
    const prog = loadProgress();
    const unlocked = JSON.parse(localStorage.getItem(UNLOCK_KEY) || "[]");
    const hasUnlocked4 = Array.isArray(unlocked) && unlocked.includes(4);
    const progressedTo4 = prog.currentWeek >= 4 || (prog.completedWeeks || []).includes(4) || (prog.skippedWeeks || []).includes(4);
    return hasUnlocked4 || progressedTo4;
  } catch {
    return false;
  }
}

function loadPostsAll() {
  try {
    const raw = localStorage.getItem(DIARY_POSTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function savePostsAll(obj) { try { localStorage.setItem(DIARY_POSTS_KEY, JSON.stringify(obj)); } catch {} }

function migrateLegacyIfNeeded() {
  try {
    const raw = localStorage.getItem(DIARY_LEGACY_KEY);
    if (!raw) return;
    const data = JSON.parse(raw); // {dateKey: text}
    const posts = loadPostsAll();
    const now = Date.now();
    Object.entries(data).forEach(([dateKey, text]) => {
      if (!text) return;
      const arr = posts[dateKey] || [];
      if (arr.length > 0) return; // don't duplicate
      arr.push({ id: `${dateKey}-legacy`, ts: now, title: "Journal", type: "journaling", content: text, imageUrl: "" });
      posts[dateKey] = arr;
    });
    savePostsAll(posts);
    try { localStorage.removeItem(DIARY_LEGACY_KEY); } catch {}
  } catch {}
}

function escapeHtml(s) { return s.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;"); }
function renderContent(text) {
  let safe = escapeHtml(text || "");
  safe = safe.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  safe = safe.replace(/\*(.*?)\*/g, "<em>$1</em>");
  const parts = safe.split(/\n{2,}/);
  const html = parts.map(p => `<p class=\"mb-2 leading-relaxed\">${p.replace(/\n/g, "<br/>")}</p>`).join("");
  return { __html: html };
}

function rgbToRgba(rgb, alpha) {
  const match = String(rgb || "").match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/i);
  if (!match) return `rgba(45,212,191,${alpha})`;
  return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
}

function postToneStyle(post) {
  const type = String(post?.type || "journaling").toLowerCase();
  if (type === "relapse") {
    return {
      background: "rgba(127, 29, 29, 0.72)",
      border: "1px solid rgba(248, 113, 113, 0.72)",
      boxShadow: "0 10px 24px rgba(248, 113, 113, 0.20)",
    };
  }
  if (type === "trigger") {
    return {
      background: "rgba(120, 53, 15, 0.74)",
      border: "1px solid rgba(251, 191, 36, 0.75)",
      boxShadow: "0 10px 24px rgba(251, 191, 36, 0.20)",
    };
  }
  if (typeof post?.mood === "number") {
    const mood = Math.max(1, Math.min(10, Number(post.mood)));
    const c = moodValueToColor(mood);
    return {
      background: rgbToRgba(c, 0.26),
      border: `1px solid ${rgbToRgba(c, 0.72)}`,
      boxShadow: `0 10px 24px ${rgbToRgba(c, 0.24)}`,
    };
  }
  return {
    background: "rgba(13, 148, 136, 0.33)",
    border: "1px solid rgba(45, 212, 191, 0.72)",
    boxShadow: "0 10px 24px rgba(45, 212, 191, 0.20)",
  };
}

export default function DiaryDay() {
  const nav = useNavigate();
  const available = useMemo(() => diaryAvailable(), []);
  const [search] = useSearchParams();
  const [date, setDate] = useState(() => {
    const fromQuery = search.get("date");
    if (fromQuery) { const parts = fromQuery.split("-").map(Number); if (parts.length === 3) return new Date(parts[0], parts[1]-1, parts[2]); }
    return new Date();
  });
  const [open, setOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [debugVisible, setDebugVisible] = useState(false);

  useEffect(() => { migrateLegacyIfNeeded(); }, []);

  useEffect(() => {
    const all = loadPostsAll();
    const dk = toDateKey(date);
    const dayPosts = (all[dk] || []).slice().sort((a,b)=>b.ts - a.ts);
    setPosts(dayPosts);
    // refresh aggregates for today and lock past days
    finalizePastDays();
  }, [date]);

  const changeBy = (days) => { const d = new Date(date); d.setDate(d.getDate() + days); setDate(d); };

  if (!available) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100 text-xl font-semibold"><Lock className="h-5 w-5" /> Diary</div>
          <p className="mt-2 text-slate-600 dark:text-slate-300 text-sm">The Diary becomes available when Week 4 is unlocked. You can unlock Week 4 from the Tracker page (moderator password) or by progressing to Week 4.</p>
          <div className="mt-4"><Button variant="secondary" onClick={() => nav("/tracker")}>Go to Tracker</Button></div>
        </div>
      </div>
    );
  }

  const title = date.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const titleFirst = title ? title.charAt(0) : "";
  const titleRest = title ? title.slice(1) : "";

  useEffect(() => {
    const onTw = () => {
      finalizePastDays();
    };
    const onDiaryChanged = () => {
      try { finalizePastDays(); } catch {}
      try {
        const all = loadPostsAll();
        const dk = toDateKey(date);
        const dayPosts = (all[dk] || []).slice().sort((a,b)=>b.ts - a.ts);
        setPosts(dayPosts);
      } catch {}
    };
    window.addEventListener("rl-timewarp-changed", onTw);
    window.addEventListener("rl-diary-changed", onDiaryChanged);
    return () => {
      window.removeEventListener("rl-timewarp-changed", onTw);
      window.removeEventListener("rl-diary-changed", onDiaryChanged);
    };
  }, [date]);

  const accentColor = 'rgba(45,212,191,0.25)'; // use brand teal as outline color globally

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:py-10">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 select-none">
          <button
            type="button"
            onClick={() => setDebugVisible((v)=>!v)}
            className="inline-block mr-0 pr-0 focus:outline-none"
            aria-label="Toggle debug"
            style={{ background: 'transparent', border: 0 }}
          >
            {titleFirst}
          </button>
          {titleRest}
        </h1>
        <div className="flex items-center gap-2">
          <Button className="btn-primary hidden sm:inline-flex" onClick={() => nav(`/diary/new?date=${encodeURIComponent(toDateKey(date))}`)}>
            <Plus className="h-4 w-4 mr-2" /> New Journal Post
          </Button>
      {debugVisible && (
        <div className="mt-4 rounded-lg border border-fuchsia-300/40 dark:border-fuchsia-700/40 bg-fuchsia-50/40 dark:bg-fuchsia-900/10 p-3">
          <div className="text-xs uppercase tracking-wide text-fuchsia-700 dark:text-fuchsia-300 mb-2">Debug Timewarp</div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => advanceDays(1)}>+1 day</Button>
            <Button variant="outline" size="sm" onClick={() => advanceWeeks(1)}>+1 week</Button>
            <Button variant="outline" size="sm" onClick={() => advanceThirtyDays()}>+30 days</Button>
            <Button variant="outline" size="sm" onClick={() => advanceYearPlus()}>+367 days</Button>
            <Button variant="destructive" size="sm" onClick={() => resetOffset()}>Reset</Button>
          </div>
        </div>
      )}

          <Button variant="ghost" size="icon" aria-label="Previous day" onClick={() => changeBy(-1)}><ChevronLeft className="h-5 w-5" /></Button>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Change date"><CalendarIcon className="h-5 w-5" /></Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="p-0"><Calendar mode="single" selected={date} onSelect={(d) => { if (d) { setDate(d); setOpen(false); } }} initialFocus /></PopoverContent>
          </Popover>
          <Button variant="ghost" size="icon" aria-label="Next day" onClick={() => changeBy(1)}><ChevronRight className="h-5 w-5" /></Button>
        </div>
      </div>
      <div className="mt-3 border-b border-slate-200 dark:border-slate-800" />

      {/* Top Mood Box */}
      {(() => {
        const dm = loadDailyMoodMap();
        const key = toDateKey(date);
        const dayAvg = dm[key]?.avg ?? null;
        const color = typeof dayAvg === 'number' ? moodValueToColor(dayAvg) : '#94a3b8';
        const emoji = typeof dayAvg === 'number' ? (dayAvg >= 8 ? '😄' : dayAvg >= 6 ? '🙂' : dayAvg >= 4 ? '😐' : dayAvg >= 2 ? '😟' : '😡') : '🙂';
        return (
          <div className="mt-6 rounded-2xl bg-slate-900/60 backdrop-blur-sm p-4 shadow-[0_8px_30px_rgba(0,255,200,0.08)] relative overflow-hidden" style={{ border: '1px solid rgba(45,212,191,0.25)' }}>
            <div className="flex items-center gap-4">
              <div className="text-3xl" style={{ color }}>{emoji}</div>
              <div className="flex-1">
                <div className="h-3 rounded-full bg-slate-700/60 overflow-hidden">
                  <div className="h-3" style={{ width: `${typeof dayAvg === 'number' ? ((dayAvg-1)/9*100) : 0}%`, background: 'linear-gradient(90deg,#FF0000 0%, #FFD93D 50%, #00C853 100%)' }} />
                </div>
              </div>
              <div className="ml-4 min-w-[56px] text-right text-xl font-extrabold" style={{ color }}>{typeof dayAvg === 'number' ? dayAvg.toFixed(1) : '–'}/10</div>
            </div>
          </div>
        );
      })()}

      {/* Mood Trends section moved to bottom */}

      {/* Today’s Entries section */}
      <div className="mt-6">
        <div className="text-lg font-semibold text-slate-100 mb-2">Today’s Entries</div>
        <div className="space-y-3">
          {posts.length === 0 && (
            <div className="rounded-xl border bg-slate-900/40 p-4 text-slate-300" style={{ border:`1px solid ${accentColor}` }}>
              No journal entries yet. Start by adding your first one!
            </div>
          )}
          {posts.map((p) => (
            <div key={p.id} className="w-full rounded-2xl p-4" style={postToneStyle(p)} data-testid={`diary-day-post-card-${p.id}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-amber-400">★</div>
                    <div className="text-slate-900 dark:text-slate-100 font-semibold">{p.title || 'Untitled'}</div>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-700 dark:text-slate-400">
                    {new Date(p.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {p.editedAt ? ` · Edited ${new Date(p.editedAt).toLocaleDateString([], { year:'numeric', month:'short', day:'2-digit' })}` : ''}
                  </div>
                  {p.content ? (
                    <div className="mt-2 prose prose-sm dark:prose-invert max-w-none text-slate-900 dark:text-slate-200/90" dangerouslySetInnerHTML={renderContent(p.content)} />
                  ) : null}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => nav(`/diary/edit?id=${encodeURIComponent(p.id)}&date=${encodeURIComponent(toDateKey(date))}`)} data-testid={`diary-day-post-edit-${p.id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <Button className="btn-primary w-full sm:w-auto sm:px-8 sm:py-6 text-base" onClick={() => nav(`/diary/new?date=${encodeURIComponent(toDateKey(date))}`)}>
          <Plus className="h-4 w-4 mr-2" /> Add Journal Entry
        </Button>
      </div>
    </div>
  );
}